import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { ErrorState, Spinner } from '../components/ui.jsx';
import { useFetch, useTitle } from '../lib/hooks.js';
import { formatDate, formatDateTime, formatXaf, titleCase } from '../lib/format.js';
import { saveElementAsPdf } from '../lib/pdf.js';
import { useMeta } from '../state/MetaContext.jsx';
import { useToast } from '../state/ToastContext.jsx';

/**
 * A rent receipt sized for A5, which is what a receipt book page is. Printing is
 * the browser's own print to PDF: no PDF library to ship, no server-side rendering,
 * and the landlord can print two to a sheet or save the file.
 */
const PrintableReceipt = () => {
  const { id } = useParams();
  const { paymentMethods } = useMeta();
  const toast = useToast();
  const { data, loading, error, reload } = useFetch(`/payments/${id}/receipt`, [id]);
  const receiptRef = useRef(null);
  const [saving, setSaving] = useState(false);

  useTitle(data ? `Receipt ${data.receipt.receiptNumber}` : 'Receipt');

  // A print stylesheet is not enough on its own: the page has to lose the site
  // furniture, so the body gets a class while this page is mounted.
  useEffect(() => {
    document.body.classList.add('printing');
    return () => document.body.classList.remove('printing');
  }, []);

  const downloadPDF = async () => {
    // Rendering the sheet to a canvas blocks the page for a moment, so a second
    // click while one run is in flight would only pile up work and look frozen.
    if (!receiptRef.current || !data || saving) return;

    setSaving(true);
    try {
      await saveElementAsPdf(receiptRef.current, {
        margin: 5,
        filename: `receipt-${data.receipt.receiptNumber}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a5', orientation: 'portrait' }
      });
    } catch (cause) {
      console.error('Receipt PDF failed:', cause);
      toast.error('Could not build the PDF. Use the Print button and save as PDF instead.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner />;
  if (error) {
    return (
      <div className="container section">
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  }

  const { receipt } = data;
  const coverage =
    receipt.coverageFromMonth && receipt.coverageToMonth
      ? receipt.coverageFromMonth === receipt.coverageToMonth
        ? receipt.coverageFromMonth
        : `${receipt.coverageFromMonth} to ${receipt.coverageToMonth}`
      : null;
  const methodLabel = paymentMethods.find((method) => method.slug === receipt.method)?.label || titleCase(receipt.method);
  const scopedBalance =
    receipt.balanceAfterCategory === null || receipt.balanceAfterCategory === undefined
      ? receipt.balanceAfter
      : receipt.balanceAfterCategory;
  const scopedKindLabel = receipt.balanceKind ? titleCase(receipt.balanceKind.replace(/_/g, ' ')) : 'Account';

  return (
    <div className="print-page">
      <div className="print-toolbar">
        <Link to="/landlord/rent" className="btn btn-ghost btn-sm">
          Back
        </Link>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => window.print()}>
          Print
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={downloadPDF} disabled={saving}>
          {saving ? 'Preparing PDF...' : '↓ Download PDF'}
        </button>
      </div>

      <article ref={receiptRef} className={`receipt-sheet ${receipt.voided ? 'is-void' : ''}`}>
        {receipt.voided ? <div className="void-stamp">Cancelled</div> : null}

        <header className="receipt-head">
          <div>
            <h1>Rent receipt</h1>
            <p className="receipt-number">{receipt.receiptNumber}</p>
          </div>
          <div className="receipt-issuer">
            {receipt.landlord.logoUrl ? <img className="doc-logo" src={receipt.landlord.logoUrl} alt="" /> : null}
            <strong>{receipt.landlord.name}</strong>
            {receipt.landlord.contact ? <span>{receipt.landlord.contact}</span> : null}
            <span className="muted">{receipt.platform.name}</span>
          </div>
        </header>

        <table className="receipt-body">
          <tbody>
            <tr>
              <th scope="row">Received from</th>
              <td>
                <strong>{receipt.tenant.name}</strong>
                {receipt.tenant.phone ? <span className="muted"> {receipt.tenant.phone}</span> : null}
              </td>
            </tr>
            <tr>
              <th scope="row">The sum of</th>
              <td>
                <strong className="receipt-amount">{formatXaf(receipt.amount)}</strong>
                <span className="receipt-words">({receipt.amountInWords})</span>
              </td>
            </tr>
            <tr>
              <th scope="row">Being for</th>
              <td>
                {receipt.forCharge ? receipt.forCharge.label : 'Rent account'}
                {receipt.note ? <span className="muted"> - {receipt.note}</span> : null}
              </td>
            </tr>
            <tr>
              <th scope="row">Premises</th>
              <td>
                {receipt.issuedFor.unit ? `${receipt.issuedFor.unit}, ` : ''}
                {receipt.issuedFor.property}
                {receipt.issuedFor.address ? <span className="muted"> - {receipt.issuedFor.address}</span> : null}
              </td>
            </tr>
            <tr>
              <th scope="row">Paid by</th>
              <td>
                {methodLabel}
                {receipt.reference ? <span className="muted"> - reference {receipt.reference}</span> : null}
              </td>
            </tr>
            {coverage ? (
              <tr>
                <th scope="row">Coverage period</th>
                <td>{coverage}</td>
              </tr>
            ) : null}
            <tr>
              <th scope="row">Date received</th>
              <td>{formatDate(receipt.paidAt)}</td>
            </tr>
            <tr>
              <th scope="row">
                {receipt.balanceKind ? `${scopedKindLabel} balance after this payment` : 'Balance after this payment'}
              </th>
              <td>
                {scopedBalance > 0
                  ? `${formatXaf(scopedBalance)} still owing`
                  : scopedBalance < 0
                    ? `${formatXaf(-scopedBalance)} paid in advance`
                    : 'Nothing owing'}
              </td>
            </tr>
          </tbody>
        </table>

        <footer className="receipt-foot">
          <div className="receipt-sign">
            <span className="receipt-sign-line" />
            <span>For {receipt.landlord.name}</span>
            {receipt.landlord.signatory && receipt.landlord.signatory !== receipt.landlord.name ? (
              <span className="muted">{receipt.landlord.signatory}</span>
            ) : null}
          </div>
          <div className="receipt-meta">
            <span>Issued {formatDateTime(receipt.createdAt)}</span>
            {receipt.providerTxnId ? (
              <span>Confirmed automatically by {methodLabel}</span>
            ) : receipt.recordedByName ? (
              <span>Recorded by {receipt.recordedByName}</span>
            ) : null}
            <span className="muted">
              This receipt was generated by {receipt.platform.name} and can be verified against the rent statement of
              both parties.
            </span>
          </div>
        </footer>

        {receipt.voided ? (
          <p className="receipt-void-note">
            This receipt was cancelled{receipt.voidReason ? `: ${receipt.voidReason}` : '.'} It no longer counts towards
            what has been paid.
          </p>
        ) : null}
      </article>
    </div>
  );
};

export default PrintableReceipt;
